import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, AttachFile, ReceiptLong } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/api';
import ProviderPriceBreakdown from '../components/ProviderPriceBreakdown';
import { useAuth } from '../contexts/AuthContext';
import { salesTaxOptionsForJob } from '../utils/salesTax';

const FEE_PERCENT = 1;
const DESC_MAX = 500;
const ATTACHMENT_MAX_MB = 25;
const ATTACHMENT_ACCEPT = '.pdf,image/jpeg,image/png,image/gif,image/webp';

interface EstimateAttachment {
  name: string;
  mimeType?: string;
  viewUrl?: string | null;
  uploadedAt?: string;
  missing?: boolean;
}

interface EligibleCustomer {
  existingCustomerId: string;
  customerId: string;
  name: string;
  email?: string;
  phone?: string;
  address?: { street?: string; city?: string; state?: string; zipCode?: string };
}

interface AdditionalRow {
  _id: string;
  status: string;
  documentLabel?: string;
  description?: string;
  scheduledDate?: string;
  serviceType?: { name?: string; description?: string };
  customerId?: { firstName?: string; lastName?: string };
  bid?: { _id: string; price: number; status: string; validUntil?: string };
  address?: { street?: string; city?: string; zipCode?: string };
  providerEstimateAttachment?: EstimateAttachment | null;
}

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

/** Estimates whose 7-day window lapsed show as Expired even before the request itself is marked expired. */
const isExpiredRow = (row: AdditionalRow) =>
  row.status === 'expired_unaccepted' || (['pending', 'bidding'].includes(row.status) && row.bid?.status === 'expired');

const statusBadge = (row: AdditionalRow): { label: string; color: ChipColor } => {
  if (isExpiredRow(row)) return { label: 'Expired', color: 'error' };
  switch (row.status) {
    case 'pending':
    case 'bidding':
      return { label: 'Waiting for customer', color: 'warning' };
    case 'accepted':
      return { label: 'Accepted — ready to schedule', color: 'info' };
    case 'scheduled':
      return { label: 'Scheduled', color: 'primary' };
    case 'in-progress':
      return { label: 'In progress', color: 'primary' };
    case 'completed':
      return { label: 'Completed', color: 'success' };
    case 'cancelled':
      return { label: 'Withdrawn', color: 'default' };
    default:
      return { label: row.status.replace(/[_-]/g, ' '), color: 'default' };
  }
};

const ProviderAdditionalServicesPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<AdditionalRow[]>([]);
  const [eligible, setEligible] = useState<EligibleCustomer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    existingCustomerId: '',
    title: '',
    description: '',
    price: '',
  });
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [existingAttachment, setExistingAttachment] = useState<EstimateAttachment | null>(null);
  const [attachmentBusy, setAttachmentBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [listRes, eligRes] = await Promise.all([
        apiService.get<AdditionalRow[]>('/additional-services'),
        apiService.get<EligibleCustomer[]>('/additional-services/eligible-customers'),
      ]);
      if (listRes.success && listRes.data) setRows(listRes.data);
      if (eligRes.success && eligRes.data) setEligible(eligRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load Additional Services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ existingCustomerId: eligible[0]?.existingCustomerId || '', title: '', description: '', price: '' });
    setPendingFile(null);
    setExistingAttachment(null);
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: AdditionalRow) => {
    const title =
      row.serviceType?.name?.includes(':')
        ? row.serviceType.name.split(':').slice(1).join(':').trim()
        : '';
    setEditingId(row._id);
    setForm({
      existingCustomerId: '',
      title,
      description: row.description || row.serviceType?.description || '',
      price: String(row.bid?.price || ''),
    });
    setPendingFile(null);
    setExistingAttachment(row.providerEstimateAttachment || null);
    setError(null);
    setDialogOpen(true);
  };

  const handleAttachmentSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
      setError(`File must be ${ATTACHMENT_MAX_MB}MB or smaller`);
      return;
    }

    // For existing pending estimates, upload immediately so the card shows the document
    // without waiting for Save & Resend. Accepted estimates must be saved first (status reset).
    const canUploadNow =
      !!editingId &&
      rows.some((r) => r._id === editingId && ['pending', 'bidding'].includes(r.status));

    if (canUploadNow && editingId) {
      try {
        setAttachmentBusy(true);
        setError(null);
        const uploaded = await uploadAttachment(editingId, file);
        setExistingAttachment(uploaded);
        setPendingFile(null);
        setSuccess('Estimate document attached');
        await load();
      } catch (err: any) {
        setPendingFile(file);
        setError(
          err.message ||
            'Document upload failed. You can still Save & Resend and we will try again.',
        );
      } finally {
        setAttachmentBusy(false);
      }
      return;
    }

    setPendingFile(file);
    setError(null);
  };

  const uploadAttachment = async (serviceRequestId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await apiService.post<{ providerEstimateAttachment: EstimateAttachment }>(
      `/additional-services/${serviceRequestId}/estimate-attachment`,
      fd,
      { timeout: 120000 },
    );
    if (!res.success) throw new Error(res.error || 'Failed to upload estimate document');
    return res.data?.providerEstimateAttachment || null;
  };

  const removeAttachment = async () => {
    if (pendingFile) {
      setPendingFile(null);
      return;
    }
    if (!editingId || !existingAttachment) return;
    try {
      setAttachmentBusy(true);
      setError(null);
      const res = await apiService.delete(`/additional-services/${editingId}/estimate-attachment`);
      if (!res.success) throw new Error(res.error || 'Failed to remove document');
      setExistingAttachment(null);
      setSuccess('Estimate document removed');
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to remove document');
    } finally {
      setAttachmentBusy(false);
    }
  };

  const createEstimate = async () => {
    try {
      setSaving(true);
      setError(null);
      if (!form.title.trim() || !form.description.trim() || !Number(form.price)) {
        setError('Title, description, and price are required');
        return;
      }
      if (form.description.trim().length > DESC_MAX) {
        setError(`Description must be ${DESC_MAX} characters or fewer`);
        return;
      }

      let serviceRequestId = editingId;
      let successMessage = '';

      if (editingId) {
        const res = await apiService.put(`/additional-services/${editingId}`, {
          title: form.title.trim(),
          description: form.description.trim(),
          price: Number(form.price),
        });
        if (!res.success) throw new Error(res.error || 'Failed to update estimate');
        successMessage = 'Updated estimate sent. Customer must accept the new price before scheduling.';
      } else {
        const selected = eligible.find((e) => e.existingCustomerId === form.existingCustomerId);
        if (!selected) {
          setError('Select a customer');
          return;
        }
        const res = await apiService.post<{ serviceRequest: { _id: string } }>('/additional-services', {
          existingCustomerId: selected.existingCustomerId,
          customerId: selected.customerId,
          title: form.title.trim(),
          description: form.description.trim(),
          price: Number(form.price),
        });
        if (!res.success) throw new Error(res.error || 'Failed to create estimate');
        serviceRequestId = res.data?.serviceRequest?._id || null;
        successMessage = 'Estimate sent to the customer. They have 7 days to accept.';
      }

      let attachmentError: string | null = null;
      let attachmentUploaded = false;
      if (pendingFile && serviceRequestId) {
        try {
          await uploadAttachment(serviceRequestId, pendingFile);
          attachmentUploaded = true;
        } catch (uploadErr: any) {
          attachmentError =
            uploadErr?.message ||
            'Estimate was saved, but the document failed to upload. Open Edit and attach it again.';
        }
      }

      setPendingFile(null);
      setExistingAttachment(null);
      setEditingId(null);
      setDialogOpen(false);

      if (attachmentError) {
        setSuccess(successMessage);
        setError(attachmentError);
      } else if (attachmentUploaded) {
        setSuccess(`${successMessage} Document attached.`);
      } else {
        setSuccess(successMessage);
      }
      await load();
    } catch (err: any) {
      // Keep dialog open only when the estimate itself failed to save
      setError(err.message || 'Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (id: string) => {
    try {
      setError(null);
      const res = await apiService.post(`/additional-services/${id}/withdraw`);
      if (!res.success) throw new Error(res.error || 'Withdraw failed');
      setSuccess('Estimate withdrawn');
      await load();
    } catch (err: any) {
      setError(err.message || 'Withdraw failed');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLong color="primary" />
            Additional Services
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Send one-time estimates (Fall Cleanup, mulch, aeration, etc.). Flat {FEE_PERCENT}% LawnHub fee. Customer accepts → you schedule.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate} disabled={eligible.length === 0}>
          New Estimate
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {eligible.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Additional Services are only available for customers who already have an accepted mowing, weed control, or snow removal service with you.
        </Alert>
      )}

      <Grid container spacing={2}>
        {rows.length === 0 ? (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No Additional Services yet. Create an estimate for an eligible customer.</Typography>
            </Card>
          </Grid>
        ) : (
          rows.map((row) => {
            const isEstimate = ['pending', 'bidding'].includes(row.status);
            const isExpired = isExpiredRow(row);
            const badge = statusBadge(row);
            return (
              <Grid item xs={12} md={6} key={row._id}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box>
                        <Typography fontWeight={700}>{row.serviceType?.name || 'Additional Services'}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.customerId?.firstName} {row.customerId?.lastName}
                          {row.address?.street ? ` · ${row.address.street}` : ''}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={row.documentLabel || (isEstimate || isExpired ? 'Estimate' : 'Invoice')}
                        color={isEstimate || isExpired ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
                      {row.description || row.serviceType?.description}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1.5 }}>
                      {money(row.bid?.price || 0)}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={badge.label} color={badge.color} />
                      {row.scheduledDate && (
                        <Typography variant="caption" color="text.secondary">
                          Scheduled {new Date(row.scheduledDate).toLocaleDateString()}
                        </Typography>
                      )}
                      {isExpired && (
                        <Typography variant="caption" color="text.secondary">
                          The customer didn&apos;t accept in time. Resend to give them another 7 days.
                        </Typography>
                      )}
                    </Stack>
                    {row.providerEstimateAttachment?.name && (
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          icon={<AttachFile />}
                          label={row.providerEstimateAttachment.name}
                          variant="outlined"
                          color="primary"
                        />
                        {row.providerEstimateAttachment.viewUrl && (
                          <Button
                            size="small"
                            href={row.providerEstimateAttachment.viewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </Button>
                        )}
                        {row.providerEstimateAttachment.missing && (
                          <Typography variant="caption" color="error">
                            File missing. Use Edit / Resend to upload it again.
                          </Typography>
                        )}
                      </Stack>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                      {(isEstimate || isExpired || row.status === 'accepted') && (
                        <Button size="small" variant={isExpired ? 'contained' : 'text'} onClick={() => openEdit(row)}>
                          Edit / Resend
                        </Button>
                      )}
                      {isEstimate && (
                        <Button size="small" color="error" onClick={() => withdraw(row._id)}>
                          Withdraw
                        </Button>
                      )}
                      {row.status === 'accepted' && (
                        <Button size="small" component={RouterLink} to="/provider" variant="outlined">
                          Schedule on Dashboard
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Edit & Resend Estimate' : 'New Additional Services Estimate'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && dialogOpen && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {!editingId && (
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  label="Customer"
                  value={form.existingCustomerId}
                  onChange={(e) => setForm((f) => ({ ...f, existingCustomerId: e.target.value }))}
                >
                  {eligible.map((c) => (
                    <MenuItem key={c.existingCustomerId} value={c.existingCustomerId}>
                      {c.name}
                      {c.address?.street ? ` — ${c.address.street}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {editingId && (
              <Alert severity="warning">
                Saving changes sends a new Estimate. The customer must accept again before you can schedule.
              </Alert>
            )}
            <TextField
              label="Service title"
              placeholder="Fall Cleanup"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              required
              helperText="Shows on schedule as “Additional Services: your title”"
            />
            <TextField
              label="Job description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, DESC_MAX) }))}
              fullWidth
              required
              multiline
              minRows={3}
              helperText={`${form.description.length}/${DESC_MAX}`}
            />
            <TextField
              label="Price"
              type="number"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              fullWidth
              required
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            {Number(form.price) > 0 && (
              <ProviderPriceBreakdown
                servicePrice={Number(form.price)}
                platformFeePercent={FEE_PERCENT}
                label="Price breakdown"
                {...salesTaxOptionsForJob(
                  (user as any)?.salesTaxRates,
                  eligible.find((e) => e.existingCustomerId === form.existingCustomerId)?.address
                )}
              />
            )}
            <Box>
              <Typography variant="subtitle2">Estimate document (optional)</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                PDF or image, up to {ATTACHMENT_MAX_MB}MB — attach a detailed quote if you use estimate software or have multiple options.
              </Typography>
              {(existingAttachment?.name || pendingFile) && (
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <AttachFile fontSize="small" color="action" />
                  <Typography variant="body2">{pendingFile?.name || existingAttachment?.name}</Typography>
                  {existingAttachment?.viewUrl && !pendingFile && (
                    <Button
                      size="small"
                      href={existingAttachment.viewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View
                    </Button>
                  )}
                  {existingAttachment?.missing && !pendingFile && (
                    <Typography variant="caption" color="error">
                      File missing. Replace it to send the customer a working copy.
                    </Typography>
                  )}
                  <Button
                    size="small"
                    color="error"
                    onClick={removeAttachment}
                    disabled={attachmentBusy || saving}
                  >
                    Remove
                  </Button>
                </Stack>
              )}
              <Button variant="outlined" component="label" size="small" disabled={saving || attachmentBusy}>
                {existingAttachment || pendingFile ? 'Replace file' : 'Attach file'}
                <input
                  type="file"
                  hidden
                  accept={ATTACHMENT_ACCEPT}
                  onChange={handleAttachmentSelect}
                />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={createEstimate} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingId ? 'Save & Resend' : 'Send Estimate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProviderAdditionalServicesPage;
